import { createContext } from 'react';
import {
	TFileId,
	ICreateModifierValidationError,
	IItemForGetShortInfo,
	IItemForGetShortInfoWithStatus,
	IServerError,
	TLanguageIDs,
	IMenuTranslation,
	IDefaultLanguage,
	ICreateModifier,
	ITotalPrice,
} from 'types';

export type TCreateModifierVisibleForm = 'mainForm' | 'translations';
export type TTypeOfCreateModifierModalWindow =
	| null
	| 'toConfirmFieldsClearing'
	| 'toConfirmSubmit'
	| 'success'
	| 'error'
	| 'defaultTranslationIsEmpty';

export interface ICreateModifierState {
	visibleForm: TCreateModifierVisibleForm;
	fileId: TFileId;
	fetchedCustomers: IItemForGetShortInfo[];
	selectedCustomer: Nullable<string>;
	fetchedRestaurants: IItemForGetShortInfoWithStatus[];
	restaurantsForPrice: string[];
	price: string;
	totalPrices: ITotalPrice[];
	fetchedModifierGroups: IItemForGetShortInfo[];
	selectedModifierGroups: string[];
	name: string;
	description: string;
	isDisabledInRestaurants: string[];
	isRestrictedInRestaurants: string[];
	isPreselectedInRestaurants: string[];
	quantityRegulationRestaurants: string[];
	quantityMaxLimit: number | null;
	defaultCustomerLanguage: Nullable<IDefaultLanguage>;
	selectedTranslationTab: TLanguageIDs | null;
	translations: IMenuTranslation[];
	activationSchedule: string | null;
	deactivationSchedule: string | null;
	dataForSubmit: ICreateModifier | null;
	typeOfModalWindow: TTypeOfCreateModifierModalWindow;
	errorResponseData: IServerError<ICreateModifierValidationError> | null;
}

export const createModifierInitialState: ICreateModifierState = {
	visibleForm: 'mainForm',
	fileId: null,
	fetchedCustomers: [],
	selectedCustomer: null,
	fetchedRestaurants: [],
	restaurantsForPrice: [],
	price: '',
	totalPrices: [],
	fetchedModifierGroups: [],
	selectedModifierGroups: [],
	name: '',
	description: '',
	isDisabledInRestaurants: [],
	isRestrictedInRestaurants: [],
	isPreselectedInRestaurants: [],
	quantityRegulationRestaurants: [],
	quantityMaxLimit: null,
	defaultCustomerLanguage: null,
	selectedTranslationTab: null,
	translations: [],
	activationSchedule: null,
	deactivationSchedule: null,
	dataForSubmit: null,
	typeOfModalWindow: null,
	errorResponseData: null,
};

export interface ICreateModifierContext extends ICreateModifierState {
	changeVisibleForm(value: TCreateModifierVisibleForm): void;
	setFileId(value: TFileId): void;
	setFetchedCustomers(value: IItemForGetShortInfo[]): void;
	setCustomer(value: Nullable<string>): void;
	setFetchedModifierGroups(value: IItemForGetShortInfo[]): void;
	setModifierGroups(value: string[]): void;
	changeName(value: string): void;
	changeDescription(value: string): void;
	setFetchedRestaurants(value: IItemForGetShortInfoWithStatus[]): void;
	setRestaurantsForPrice(value: string[]): void;
	changePrice(value: string): void;
	addPrices(value: ITotalPrice[]): void;
	removePrice(value: string[]): void;
	setDisabledInRestaurants(value: string[]): void;
	setRestrictedInRestaurants(value: string[]): void;
	setPreselectedInRestaurants(value: string[]): void;
	setQuantityRegulationRestaurants(value: string[]): void;
	changeQuantityMaxLimit(value: number): void;
	setFetchedTranslations(value: TLanguageIDs[], defaultCustomerLanguage: Nullable<IDefaultLanguage>): void;
	setTranslationTab(value: TLanguageIDs | null): void;
	changeNameTranslation(languageId: TLanguageIDs, value: string): void;
	changeDescriptionTranslation(languageId: TLanguageIDs, value: string): void;
	changeActivationSchedule(value: string): void;
	changeDeactivationSchedule(value: string): void;
	setDataForSubmit(value: ICreateModifier | null): void;
	changeTypeOfModalWindow(value: TTypeOfCreateModifierModalWindow): void;
	showErrorModalWithInfo(errorResponseData: IServerError<ICreateModifierValidationError> | null): void;
	resetFields(): void;
}

export const CreateModifierContext = createContext<ICreateModifierContext>({
	changeVisibleForm: () => undefined,
	setFileId: () => undefined,
	setFetchedCustomers: () => undefined,
	setCustomer: () => undefined,
	setFetchedModifierGroups: () => undefined,
	setModifierGroups: () => undefined,
	changeName: () => undefined,
	changeDescription: () => undefined,
	setFetchedRestaurants: () => undefined,
	setRestaurantsForPrice: () => undefined,
	changePrice: () => undefined,
	addPrices: () => undefined,
	removePrice: () => undefined,
	setDisabledInRestaurants: () => undefined,
	setRestrictedInRestaurants: () => undefined,
	setPreselectedInRestaurants: () => undefined,
	setQuantityRegulationRestaurants: () => undefined,
	changeQuantityMaxLimit: () => undefined,
	setFetchedTranslations: () => undefined,
	setTranslationTab: () => undefined,
	changeNameTranslation: () => undefined,
	changeDescriptionTranslation: () => undefined,
	changeActivationSchedule: () => undefined,
	changeDeactivationSchedule: () => undefined,
	setDataForSubmit: () => undefined,
	changeTypeOfModalWindow: () => undefined,
	showErrorModalWithInfo: () => undefined,
	resetFields: () => undefined,
	...createModifierInitialState,
});
